import { getLenis } from "./lib-smooth-scroll"

/**
 * アクセシビリティ対応のアコーディオンの初期化と制御を行うクラス
 * ARIA属性とキーボード操作をサポート、レスポンシブ対応
 * @example
 * // デフォルト（document全体）で初期化
 * const accordion = new AccordionController();
 *
 * // 特定の要素内で初期化
 * const headerAccordion = new AccordionController('header');
 * const footerAccordion = new AccordionController('footer');
 *
 * // 複数のセレクタを指定
 * const mainAccordion = new AccordionController('main, aside');
 *
 * // クラスやIDセレクタも使用可能
 * const sectionAccordion = new AccordionController('.accordion-section');
 * const specificAccordion = new AccordionController('#specific-area');
 *
 * // すべて開く
 * accordion.openAll();
 * // すべて閉じる
 * accordion.closeAll();
 * // アンマウント
 * accordion.unmount();
 */
export class AccordionController {
  accordions: any[]
  allButtons: any[]
  resizeHandler: (() => void) | null
  resizeTimer: ReturnType<typeof setTimeout> | null
  breakpoint: number
  exclusive: boolean
  containerSelector: string | HTMLElement | NodeList | null
  containers: (Document | Element)[]

  /**
   * コンストラクタ
   * @param {string|HTMLElement|NodeList} containerSelector - アコーディオンを探す範囲のCSSセレクタ、要素、またはNodeList（デフォルト: document）
   * @param {Object} options - オプション設定
   * @param {boolean} options.exclusive - 排他モード（同時に1つしか開けない）（デフォルト: false）
   */
  constructor(containerSelector = null, options: { exclusive?: boolean } = {}) {
    this.accordions = []
    this.allButtons = [] // 全てのボタン（無効化されたものも含む）
    this.resizeHandler = null
    this.resizeTimer = null
    this.breakpoint = 1024
    this.exclusive = options.exclusive || false
    this.containerSelector = containerSelector
    this.containers = this.getContainers(containerSelector)

    this.init()

    // resizeイベントのリスナーを追加
    this.resizeHandler = () => {
      clearTimeout(this.resizeTimer)
      this.resizeTimer = window.setTimeout(() => {
        this.handleResize()
      }, 250) // デバウンス処理
    }
    window.addEventListener("resize", this.resizeHandler)
  }

  /**
   * コンテナ要素を取得
   * @private
   * @param {string|HTMLElement|NodeList} selector - セレクタ、要素、またはNodeList
   * @returns {NodeList|Array<HTMLElement>} コンテナ要素のリスト
   */
  getContainers(selector) {
    // selectorがnullまたはundefinedの場合はdocument全体
    if (!selector) {
      return [document]
    }

    // HTMLElementの場合
    if (selector instanceof HTMLElement) {
      return [selector]
    }

    // NodeListの場合
    if (selector instanceof NodeList) {
      return Array.from(selector) as Element[]
    }

    // 文字列セレクタの場合
    if (typeof selector === "string") {
      const elements = document.querySelectorAll(selector)
      if (elements.length === 0) {
        console.warn(`AccordionController: セレクタ "${selector}" に一致する要素が見つかりませんでした。`)
        return []
      }
      return Array.from(elements)
    }

    console.warn("AccordionController: 無効なセレクタが指定されました。document全体を使用します。")
    return [document]
  }

  /**
   * リサイズ時の処理
   * @private
   */
  handleResize() {
    const currentWidth = window.innerWidth

    this.allButtons.forEach((btnData) => {
      const shouldBeActive = this.shouldActivateAccordion(btnData.button)

      if (shouldBeActive && !btnData.isActive) {
        // アコーディオンを有効化
        this.activateAccordion(btnData)
      } else if (!shouldBeActive && btnData.isActive) {
        // アコーディオンを無効化
        this.deactivateAccordion(btnData)
      }
    })

    // 有効なアコーディオンの高さを更新
    this.updateHeights()
  }

  /**
   * アコーディオンを有効化すべきかチェック
   * @private
   * @param {HTMLElement} btn - ボタン要素
   * @returns {boolean}
   */
  shouldActivateAccordion(btn) {
    if (btn.classList.contains("acc-sp-only")) {
      return window.innerWidth <= this.breakpoint
    }
    return true // acc-sp-onlyクラスがない場合は常に有効
  }

  /**
   * アコーディオンを有効化
   * @private
   * @param {Object} btnData - ボタンデータ
   */
  activateAccordion(btnData) {
    const { button: btn, content: ans } = btnData

    // IDを設定（存在しない場合）
    if (!btn.id) {
      btn.id = this.generateId(`accordion-btn-${this.allButtons.indexOf(btnData)}`)
    }
    if (!ans.id) {
      ans.id = this.generateId(`accordion-content-${this.allButtons.indexOf(btnData)}`)
    }

    // ボタンに必要な属性を動的に追加
    btn.setAttribute("tabindex", "0")
    btn.setAttribute("role", "button")
    btn.setAttribute("aria-expanded", "false")
    btn.setAttribute("aria-controls", ans.id)

    // コンテンツに必要な属性を動的に追加
    ans.setAttribute("role", "region")
    ans.setAttribute("aria-labelledby", btn.id)
    ans.setAttribute("aria-hidden", "true")
    ans.setAttribute("inert", "") // 初期状態では非活性

    // 実際の高さを取得するために一時的に自動高さにする
    const currentTransition = ans.style.transition
    ans.style.transition = "none"
    ans.style.height = "auto"
    const originalHeight = ans.scrollHeight
    ans.style.height = "0"
    ans.offsetHeight
    ans.style.transition = currentTransition

    // イベントハンドラーを作成（トグルが<a>の場合の遷移を抑止＝アコーディオン有効時はリンク遷移させずトグルのみ）
    const clickHandler = (e: Event) => {
      e.preventDefault()
      this.toggleAccordion(btn, ans)
    }
    const keydownHandler = (e) => this.handleKeydown(e, btn, ans)
    // アコーディオン内のリンクを押したら自動で閉じる（swup遷移してもフッターは永続＝開きっぱなし防止）
    const linkClickHandler = (e: Event) => {
      const link = (e.target as HTMLElement).closest("a[href]")
      if (link && ans.contains(link)) this.closeAccordion(btn, ans)
    }

    // アコーディオンの状態を保存
    const accordionState: any = {
      element: ans,
      button: btn,
      height: originalHeight,
      isOpen: false,
      clickHandler: clickHandler,
      keydownHandler: keydownHandler,
      linkClickHandler: linkClickHandler,
      hoverTimer: null,
      hoverHandlers: null,
    }

    this.accordions.push(accordionState)

    // イベントリスナーを設定
    btn.addEventListener("click", clickHandler)
    btn.addEventListener("keydown", keydownHandler)
    ans.addEventListener("click", linkClickHandler)

    // type-hover2open: ホバーで開閉する機能
    if (btn.classList.contains("type-hover2open")) {
      const clearHoverTimer = () => {
        if (accordionState.hoverTimer) {
          clearTimeout(accordionState.hoverTimer)
          accordionState.hoverTimer = null
        }
      }

      const startCloseTimer = () => {
        clearHoverTimer()
        accordionState.hoverTimer = window.setTimeout(() => {
          this.closeAccordion(btn, ans)
          accordionState.hoverTimer = null
        }, 600)
      }

      const btnEnter = () => {
        clearHoverTimer()
        this.openAccordion(btn, ans)
      }
      const btnLeave = () => startCloseTimer()
      const ansEnter = () => clearHoverTimer()
      const ansLeave = () => startCloseTimer()

      btn.addEventListener("mouseenter", btnEnter)
      btn.addEventListener("mouseleave", btnLeave)
      ans.addEventListener("mouseenter", ansEnter)
      ans.addEventListener("mouseleave", ansLeave)

      accordionState.hoverHandlers = { btnEnter, btnLeave, ansEnter, ansLeave }
    }

    // ボタンデータを更新
    btnData.isActive = true
    btnData.accordionState = accordionState
  }

  /**
   * アコーディオンを無効化
   * @private
   * @param {Object} btnData - ボタンデータ
   */
  deactivateAccordion(btnData) {
    const { button: btn, content: ans, accordionState } = btnData

    if (!accordionState) return

    // イベントリスナーを削除
    btn.removeEventListener("click", accordionState.clickHandler)
    btn.removeEventListener("keydown", accordionState.keydownHandler)
    ans.removeEventListener("click", accordionState.linkClickHandler)

    // ホバー系イベントリスナーを削除
    if (accordionState.hoverHandlers) {
      const { btnEnter, btnLeave, ansEnter, ansLeave } = accordionState.hoverHandlers
      btn.removeEventListener("mouseenter", btnEnter)
      btn.removeEventListener("mouseleave", btnLeave)
      ans.removeEventListener("mouseenter", ansEnter)
      ans.removeEventListener("mouseleave", ansLeave)
    }
    if (accordionState.hoverTimer) {
      clearTimeout(accordionState.hoverTimer)
    }

    // 属性を削除
    btn.removeAttribute("tabindex")
    btn.removeAttribute("role")
    btn.removeAttribute("aria-expanded")
    btn.removeAttribute("aria-controls")

    ans.removeAttribute("role")
    ans.removeAttribute("aria-labelledby")
    ans.removeAttribute("aria-hidden")
    ans.removeAttribute("inert")

    // スタイルをリセット
    ans.style.height = ""
    btn.parentElement?.classList.remove("is-on")

    // アコーディオンの配列から削除
    const index = this.accordions.indexOf(accordionState)
    if (index > -1) {
      this.accordions.splice(index, 1)
    }

    // ボタンデータを更新
    btnData.isActive = false
    btnData.accordionState = null
  }

  /**
   * アコーディオンの高さを再計算
   * @private
   */
  updateHeights() {
    this.accordions.forEach((acc) => {
      // 現在のトランジション設定を保存
      const currentTransition = acc.element.style.transition

      // トランジションを一時的に無効化
      acc.element.style.transition = "none"

      // 一時的に自動高さに設定
      acc.element.style.height = "auto"

      // 高さを取得
      const newHeight = acc.element.scrollHeight

      // 新しい高さを保存
      acc.height = newHeight

      // 状態に応じて高さを設定
      acc.element.style.height = acc.isOpen ? `${newHeight}px` : "0px"

      // トランジションを元に戻す
      // 強制的にリフロー（再描画）を発生させる
      acc.element.offsetHeight
      acc.element.style.transition = currentTransition
    })
  }

  /**
   * 一意のIDを生成
   * @private
   * @param {string} prefix - IDのプレフィックス
   * @returns {string} 生成されたID
   */
  generateId(prefix) {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * ARIA属性を設定
   * @private
   * @param {HTMLElement} btn - ボタン要素
   * @param {HTMLElement} ans - コンテンツ要素
   * @param {boolean} isOpen - 開閉状態
   */
  setAriaAttributes(btn, ans, isOpen) {
    // ボタンのARIA属性
    btn.setAttribute("aria-expanded", isOpen.toString())

    // コンテンツのARIA属性
    ans.setAttribute("aria-hidden", (!isOpen).toString())

    // inert属性の制御
    if (isOpen) {
      ans.removeAttribute("inert")
    } else {
      ans.setAttribute("inert", "")
    }
  }

  /**
   * アコーディオンを初期化
   * @private
   */
  init() {
    // コンテナが見つからない場合は初期化しない
    if (this.containers.length === 0) {
      console.warn("AccordionController: 初期化するコンテナが見つかりません。")
      return
    }

    // 各コンテナ内でアコーディオンボタンを検索
    this.containers.forEach((container) => {
      const accBtns = container.querySelectorAll(".acc__txt")

      accBtns.forEach((btn, index) => {
        const ans = btn.previousElementSibling || btn.nextElementSibling
        if (!ans) return

        // 全ボタンのデータを保存（無効化されているものも含む）
        const btnData = {
          button: btn,
          content: ans,
          isActive: false,
          accordionState: null,
        }
        this.allButtons.push(btnData)

        // 現在の画面幅でアコーディオンが有効かチェック
        if (this.shouldActivateAccordion(btn)) {
          this.activateAccordion(btnData)
        }
      })
    })
  }

  /**
   * キーボードイベントのハンドリング
   * @private
   * @param {KeyboardEvent} e - キーボードイベント
   * @param {HTMLElement} btn - ボタン要素
   * @param {HTMLElement} ans - コンテンツ要素
   */
  handleKeydown(e, btn, ans) {
    // Enter または Space キーが押された場合
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault() // スクロールを防ぐ（Spaceキーの場合）
      this.toggleAccordion(btn, ans)
    }

    // Escape キーでフォーカスを外す（オプション）
    else if (e.key === "Escape") {
      btn.blur()
    }
  }

  /**
   * アコーディオンを開く
   * @private
   * @param {HTMLElement} btn - ボタン要素
   * @param {HTMLElement} ans - コンテンツ要素
   */
  openAccordion(btn, ans) {
    const parent = btn.parentElement
    if (!parent) return

    const accState = this.accordions.find((acc) => acc.element === ans)
    if (!accState || accState.isOpen) return

    parent.classList.add("is-on")
    accState.isOpen = true

    // 排他モード: 他のアコーディオンを閉じる
    if (this.exclusive) {
      this.accordions.forEach((acc) => {
        if (acc !== accState && acc.isOpen) {
          acc.element.style.height = "0px"
          acc.button.parentElement?.classList.remove("is-on")
          acc.isOpen = false
          this.setAriaAttributes(acc.button, acc.element, false)
        }
      })
    }

    // ARIA属性を更新
    this.setAriaAttributes(btn, ans, true)

    // 最新の高さを取得して使用
    const currentTransition = ans.style.transition
    ans.style.transition = "none"
    ans.style.height = "auto"
    const currentHeight = ans.scrollHeight
    ans.style.height = "0"
    ans.offsetHeight
    ans.style.transition = currentTransition
    ans.style.height = `${currentHeight}px`
    accState.height = currentHeight
  }

  /**
   * アコーディオンを閉じる
   * @private
   * @param {HTMLElement} btn - ボタン要素
   * @param {HTMLElement} ans - コンテンツ要素
   */
  closeAccordion(btn, ans) {
    const parent = btn.parentElement
    if (!parent) return

    const accState = this.accordions.find((acc) => acc.element === ans)
    if (!accState || !accState.isOpen) return

    parent.classList.remove("is-on")
    accState.isOpen = false
    ans.style.height = "0px"

    // ARIA属性を更新
    this.setAriaAttributes(btn, ans, false)

    // scrollbackクラスの処理
    if (btn.classList.contains("scrollback")) {
      // Lenis有効時は lenis.scrollTo（ネイティブ scrollTo との二重制御を防ぐ）
      const lenis = getLenis()
      const top = window.scrollY - accState.height
      if (lenis) lenis.scrollTo(top)
      else window.scrollTo({ top, behavior: "smooth" })
    }
  }

  /**
   * 個別のアコーディオンの開閉
   * @private
   * @param {HTMLElement} btn - クリックされたボタン要素
   * @param {HTMLElement} ans - 開閉する内容要素
   */
  toggleAccordion(btn, ans) {
    const parent = btn.parentElement
    if (!parent) return

    const accState = this.accordions.find((acc) => acc.element === ans)
    if (!accState) return

    if (accState.isOpen) {
      this.closeAccordion(btn, ans)
    } else {
      this.openAccordion(btn, ans)
    }
  }

  /**
   * すべてのアコーディオンを開く
   * @public
   */
  openAll() {
    this.accordions.forEach((acc) => {
      if (!acc.isOpen) {
        acc.element.style.height = `${acc.height}px`
        acc.element.parentElement?.classList.add("is-on")
        acc.isOpen = true

        // ARIA属性を更新
        this.setAriaAttributes(acc.button, acc.element, true)
      }
    })
  }

  /**
   * すべてのアコーディオンを閉じる
   * @public
   */
  closeAll() {
    this.accordions.forEach((acc) => {
      if (acc.isOpen) {
        acc.element.style.height = "0px"
        acc.element.parentElement?.classList.remove("is-on")
        acc.isOpen = false

        // ARIA属性を更新
        this.setAriaAttributes(acc.button, acc.element, false)
      }
    })
  }

  /**
   * ブレークポイントを変更
   * @public
   * @param {number} breakpoint - 新しいブレークポイント（デフォルト: 1024）
   */
  setBreakpoint(breakpoint) {
    this.breakpoint = breakpoint
    this.handleResize() // 即座にリサイズ処理を実行
  }

  /**
   * 現在管理しているアコーディオンの情報を取得
   * @public
   * @returns {Object} アコーディオンの状態情報
   */
  getInfo() {
    return {
      containerSelector: this.containerSelector,
      containersCount: this.containers.length,
      totalButtons: this.allButtons.length,
      activeAccordions: this.accordions.length,
      openAccordions: this.accordions.filter((acc) => acc.isOpen).length,
      breakpoint: this.breakpoint,
    }
  }

  /**
   * アコーディオンをアンマウントしてリソースを解放
   * @public
   */
  unmount = () => {
    // イベントリスナーを削除
    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler)
      this.resizeHandler = null
    }

    // タイマーをクリア
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer)
      this.resizeTimer = null
    }

    // 各アコーディオンのイベントリスナーを削除
    this.accordions.forEach((acc) => {
      if (acc.button) {
        if (acc.clickHandler) {
          acc.button.removeEventListener("click", acc.clickHandler)
        }
        if (acc.keydownHandler) {
          acc.button.removeEventListener("keydown", acc.keydownHandler)
        }
      }
      if (acc.element && acc.linkClickHandler) {
        acc.element.removeEventListener("click", acc.linkClickHandler)
      }
      if (acc.hoverHandlers) {
        const { btnEnter, btnLeave, ansEnter, ansLeave } = acc.hoverHandlers
        acc.button.removeEventListener("mouseenter", btnEnter)
        acc.button.removeEventListener("mouseleave", btnLeave)
        acc.element.removeEventListener("mouseenter", ansEnter)
        acc.element.removeEventListener("mouseleave", ansLeave)
      }
      if (acc.hoverTimer) {
        clearTimeout(acc.hoverTimer)
      }
    })

    // 全てのボタンから属性を削除
    this.allButtons.forEach((btnData) => {
      if (btnData.isActive) {
        this.deactivateAccordion(btnData)
      }
    })

    // 配列をクリア
    this.accordions = []
    this.allButtons = []
    this.containers = []
  }
}

// 使用例

// デフォルト（document全体）で初期化
const accordion = new AccordionController(`header`, { exclusive: true })

const faccordion = new AccordionController(`footer`)

// header要素内のアコーディオンのみ初期化
// const headerAccordion = new AccordionController('header');

// footer要素内のアコーディオンのみ初期化
// const footerAccordion = new AccordionController('footer');

// if (document.body.classList.contains("page-services-shop-products-0")) {
//   const mainAccordion = new AccordionController('.pro', { exclusive: true });
// }

// クラスセレクタを使用
// const sectionAccordion = new AccordionController('.accordion-section');

// IDセレクタを使用
// const specificAccordion = new AccordionController('#specific-area');

// 直接要素を渡すこともできる
// const element = document.querySelector('.my-container');
// const elementAccordion = new AccordionController(element);

// NodeListを渡すこともできる
// const nodeList = document.querySelectorAll('.multiple-containers');
// const nodeListAccordion = new AccordionController(nodeList);

// 現在の状態を確認
// console.log(accordion.getInfo());

// ブレークポイントを変更したい場合（デフォルト: 1024）
// accordion.setBreakpoint(768);

// すべて開く場合
// accordion.openAll();

// すべて閉じる場合
// accordion.closeAll();

// アンマウント（コンポーネントが不要になったとき）
// accordion.unmount();
